import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {isLoaded, PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {Async, Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import { SalaryDeductionOccupationClassInfo, SalaryDeductionOccupationClassModel, UnassignedSalaryDeductionOccupationClassModel, isLoaded  as isLoadedOccClass} from '../SalaryDeductionOccupationClassModel';

const riderCodes: RI.GroupRiderCode[] = [
  {
    code: 'CTPFPO',
    propertyName: '',
    displayName: '',
    units: [],
    controlType: RI.ControlType.Boolean,
  },
  {
    code: 'CTPLBR',
    propertyName: '',
    displayName: '',
    controlType: RI.ControlType.Boolean,
    units: [],
  },
  {
    code: 'CTPCTR',
    propertyName: '',
    displayName: '',
    units: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  },
  {
    code: 'CTPCTZ',
    propertyName: '',
    displayName: '',
    units: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
  },
  {
    code: 'GTLCPWP',
    propertyName: '',
    displayName: '',
    units: [],
    hidden: true,
  },
  {
    code: 'GTLCADB',
    propertyName: '',
    displayName: '',
    units: [],
    hidden: true,
  },
  {
    code: 'GTLCLTC',
    propertyName: '',
    displayName: '',
    units: [],
  },
  {
    code: 'GTLCLTCE',
    propertyName: '',
    displayName: '',
    units: [],
  },
  {
    code: 'GTLCLTCR',
    propertyName: '',
    displayName: '',
    units: [],
  },
  {
    code: 'GTLCLTCRE',
    propertyName: '',
    displayName: '',
    units: [],
  },
];

type GLTERMProps = {
  plan: StringUnion;  
  occClass: SalaryDeductionOccupationClassModel;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  situsState: StringUnion;
  type: StringUnion;
  faceAmt: StringUnion;
  policyNumber: PolicyNumber;
  premiumType: StringUnion;
  priInsured: StringUnion;
  spouseInsured: StringUnion;
  autoIssueEligibility: boolean;
  replacement: boolean;
  riderInformation: RI.default;
  commission: StringUnion;
  deathBenefit: StringUnion;
  adb: boolean;
  adbValue: StringUnion
  sbVolume: StringUnion;
  premiumWaiver: boolean;
  premiumWaiverValue: StringUnion;
  product: StringUnion;
  jstamp: boolean;
  packageName: StringUnion;
  packageList: PackageModel;
  CTPLBR: StringUnion;
  GTLCLTC: StringUnion;
  GTLCLTCE: StringUnion;
  GTLCLTCR: StringUnion;
  GTLCLTCRE: StringUnion;
};
export enum GroupGLTermType {
  accelDbTeminalIll = 'CTPLBR',
  accidentalDeathBenefit = 'GTLCADB',
  childrenGLTerm = 'CTPCTR',
  childrenNoGLTerm = 'CTPCTZ',
  futurePurchaseOption = 'CTPFPO',
  payorWaiver = 'GTLCPWP',
  longTermAccel = 'GTLCLTC',
  longTermAccelExtension = 'GTLCLTCE',
  longTermAccelRestoration = 'GTLCLTCR',
  longTermAccelRestorationExtension = 'GTLCLTCRE',
}

export class InvalidGroupGLTERM {
  public readonly key = 'InvalidGroupGLTERM';

  constructor(
    public readonly plan: StringUnion,
    public readonly occClass: SalaryDeductionOccupationClassModel,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly adbValue: StringUnion,
    public readonly sbVolume: StringUnion,
    public readonly premiumWaiver: boolean,
    public readonly premiumWaiverValue: StringUnion,
    public readonly product: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageName: StringUnion,
    public readonly packageList: PackageModel,
    public readonly CTPLBR: StringUnion,
    public readonly GTLCLTC: StringUnion,
    public readonly GTLCLTCE: StringUnion,
    public readonly GTLCLTCR: StringUnion,
    public readonly GTLCLTCRE: StringUnion,
  ) {}
}

export class ValidGroupGLTERM {
  public static createApp({
    plan,
    occClass,
    premium,
    calculatedPremium,
    cwa,
    situsState,
    type,
    faceAmt,
    policyNumber,
    premiumType,
    priInsured,
    spouseInsured,
    autoIssueEligibility,
    replacement,
    riderInformation,
    commission,
    deathBenefit,
    adb,
    adbValue,
    sbVolume,
    premiumWaiver,
    premiumWaiverValue,
    product,
    jstamp,
    packageName,
    packageList,
    CTPLBR,
    GTLCLTC,
    GTLCLTCE,
    GTLCLTCR,
    GTLCLTCRE,
  }: GLTERMProps) {
    return this.create(
      plan,
      occClass,
      premium,
      calculatedPremium,
      cwa,
      situsState,
      type,
      faceAmt,
      policyNumber,
      premiumType,
      priInsured,
      spouseInsured,
      autoIssueEligibility,
      replacement,
      riderInformation,
      commission,
      deathBenefit,
      adb,
      adbValue,
      sbVolume,
      premiumWaiver,
      premiumWaiverValue,
      product,
      jstamp,
      packageName,
      packageList,
      CTPLBR,
      GTLCLTC,
      GTLCLTCE,
      GTLCLTCR,
      GTLCLTCRE
    );
  }

  public static create(
    plan: StringUnion,
    occClass: SalaryDeductionOccupationClassModel,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    situsState: StringUnion,
    type: StringUnion,
    faceAmt: StringUnion,
    policyNumber: PolicyNumber,
    premiumType: StringUnion,
    priInsured: StringUnion,
    spouseInsured: StringUnion,
    autoIssueEligibility: boolean,
    replacement: boolean,
    riderInformation: RI.default,
    commission: StringUnion,
    deathBenefit: StringUnion,
    adb: boolean,
    adbValue: StringUnion,
    sbVolume: StringUnion,
    premiumWaiver: boolean,
    premiumWaiverValue: StringUnion,
    product: StringUnion,
    jstamp: boolean,
    packageName: StringUnion,
    packageList: PackageModel,
    CTPLBR: StringUnion,
    GTLCLTC: StringUnion,
    GTLCLTCE: StringUnion,
    GTLCLTCR: StringUnion,
    GTLCLTCRE: StringUnion,
  ) {
    return isNonEmptyString(plan) && isLoadedOccClass(occClass) && isLoaded(packageList)
      ? new ValidGroupGLTERM(
          plan,
          occClass,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderInformation,
          commission,
          deathBenefit,
          adb,
          adbValue,
          sbVolume,
          premiumWaiver,
          premiumWaiverValue,
          product,
          jstamp,
          packageName,
          packageList,
          CTPLBR,
          GTLCLTC,
          GTLCLTCE,
          GTLCLTCR,
          GTLCLTCRE,
        )
      : new InvalidGroupGLTERM(
          plan,
          occClass,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderInformation,
          commission,
          deathBenefit,
          adb,
          adbValue,
          sbVolume,
          premiumWaiver,
          premiumWaiverValue,
          product,
          jstamp,
          packageName,
          packageList,
          CTPLBR,
          GTLCLTC,
          GTLCLTCE,
          GTLCLTCR,
          GTLCLTCRE
        );
  }

  public readonly key = 'ValidGroupGLTERM';
  constructor(
    public readonly plan: StringUnion,    
    public readonly occClass: Loaded<SalaryDeductionOccupationClassInfo>,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderInformation: RI.default,
    public readonly commission: StringUnion,
    public readonly deathBenefit: StringUnion,
    public readonly adb: boolean,
    public readonly adbValue: StringUnion,
    public readonly sbVolume: StringUnion,
    public readonly premiumWaiver: boolean,
    public readonly premiumWaiverValue: StringUnion,
    public readonly product: StringUnion,
    public readonly jstamp: boolean,
    public readonly packageName: StringUnion,
    public readonly packageList: Loaded<object[]>,
    public readonly CTPLBR: StringUnion,
    public readonly GTLCLTC: StringUnion,
    public readonly GTLCLTCE: StringUnion,
    public readonly GTLCLTCR: StringUnion,
    public readonly GTLCLTCRE: StringUnion
  ) {}
}

export type GroupGLTERM = InvalidGroupGLTERM | ValidGroupGLTERM;

export type GroupGLTERMOptions<TReturn> = ToPartialMatchOptions<GroupGLTERM, 'key', TReturn>;

export const matchGroupGLTERM = <TReturn>(options: GroupGLTERMOptions<TReturn>) => (app: GroupGLTERM): TReturn => {
  switch (app.key) {
    case 'InvalidGroupGLTERM':
      return options.InvalidGroupGLTERM ? options.InvalidGroupGLTERM(app) : options.default(app);
    case 'ValidGroupGLTERM':
      return options.ValidGroupGLTERM ? options.ValidGroupGLTERM(app) : options.default(app);
  }
};

export const EmptyGroupGLTERM = new InvalidGroupGLTERM(
  new EmptyString(),
  new UnassignedSalaryDeductionOccupationClassModel(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new EmptyString(),
  false,
  new EmptyString(),
  new UnassignedPackageModel(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString()
);

export const changePlan = (plan: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    riderInformation: new RI.default(I.List<string>(), I.List<number>(), riderCodes),
  });
export const changeOccClass = (occClass: SalaryDeductionOccupationClassModel) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, occClass});
// export const updateOccClassValue = (occClassValue: string) => (app: GroupGLTERM) => {
//   return {
//     ...app,
//     saldOccClassInfo: {
//       ...app.occClass,
//       occClass: occClassValue,
//     },
//   };
// };

export const changePremium = (premium: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSitusState = (situsState: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeType = (type: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, type: NonEmptyString.create(type)});
export const changeFaceAmt = (faceAmt: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, faceAmt: NonEmptyString.create(faceAmt)});
export const changePolicyNumber = (polNum: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(polNum))});
export const changePremiumType = (preType: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, premiumType: NonEmptyString.create(preType)});
export const changePriInsured = (priInsured: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, priInsured: NonEmptyString.create(priInsured)});
export const changeSpouseInsured = (spouseInsured: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, spouseInsured: NonEmptyString.create(spouseInsured)});
export const changeAutoIssue = (autoIssueEligibility: boolean) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, autoIssueEligibility});
export const changeReplacement = (replacement: boolean) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({
    ...app,
    replacement,
    commission: !replacement
      ? new EmptyString()
      : app.commission instanceof EmptyString
      ? NonEmptyString.create('First Year Commissions')
      : app.commission,
  });
export const changeCommision = (commission: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, commission: NonEmptyString.create(commission)});

export const changeDeathBenefit = (deathBenefit: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, deathBenefit: NonEmptyString.create(deathBenefit)});
export const changeSBVolume = (vol: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, sbVolume: NonEmptyString.create(vol)});
export const changeADB = (adb: boolean) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, adb, sbVolume: new EmptyString()});
export const changeADBValue = (adbValue: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, sbVolume: NonEmptyString.create(adbValue)});
export const changeWaiver = (premiumWaiver: boolean) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, premiumWaiver});
export const changeWavierValue = (waiverValue: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, premiumWaiverValue: NonEmptyString.create(waiverValue)});
export const changeproduct = (product: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, product: NonEmptyString.create(product)});
export const changeJStamp = (jstamp: boolean) => (app: GroupGLTERM) => ValidGroupGLTERM.createApp({...app, jstamp});
export const changePackageName = (packageName: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, packageName: NonEmptyString.create(packageName)});
export const changePackageList = (packageList: Async<object[]>) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, packageList});
export const getCTPLBR = (CTPLBR: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, CTPLBR: NonEmptyString.create(CTPLBR)});
export const getGTLCLTC = (GTLCLTC: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, GTLCLTC: NonEmptyString.create(GTLCLTC)});
export const getGTLCLTCE = (GTLCLTCE: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({ ...app, GTLCLTCE: NonEmptyString.create(GTLCLTCE) });
  export const getGTLCLTCR = (GTLCLTCR: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, GTLCLTCR: NonEmptyString.create(GTLCLTCR)});
  export const getGTLCLTCRE = (GTLCLTCRE: string) => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, GTLCLTCRE: NonEmptyString.create(GTLCLTCRE)});

// type PackageItem = {
//   payorWaiver: number | null;
//   accidentalDeathBenefit: number | null;
//   [key: string]: any;
// };

export const changePackageItem = (packageItem: object) => (app: GroupGLTERM) => {
  return ValidGroupGLTERM.createApp({
    ...app,
    premiumWaiver:
      packageItem['payorWaiver'] === null ? false : Number(packageItem['payorWaiver']) === 0 ? true : false,
    adb:
      packageItem['accidentalDeathBenefit'] === null
        ? false
        : Number(packageItem['accidentalDeathBenefit']) === 0
        ? true
        : false,
  });
};

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (app: GroupGLTERM) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidGroupGLTERM.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const resetSpouseInsured = () => (app: GroupGLTERM) =>
  ValidGroupGLTERM.createApp({...app, spouseInsured: new EmptyString()});